import { useCallback, useSyncExternalStore } from 'react'

/**
 * Viewport-width questions, answered from the width and nothing else.
 *
 * Never from the user agent. A phone held in landscape, a tablet, and a half-width window on a
 * laptop are the same question — is there room to do this safely — and sniffing the device
 * answers a different one, getting the tablet wrong in both directions.
 *
 * The thresholds mirror Tailwind's, and are expressed in `rem` for the reason Tailwind expresses
 * them that way: someone who has enlarged their base font has less usable width, and a query
 * written in pixels would not notice.
 */

export const BREAKPOINTS = {
  sm: '40rem',
  md: '48rem',
  lg: '64rem',
  xl: '80rem',
} as const

export type Breakpoint = keyof typeof BREAKPOINTS

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onStoreChange)
      return () => {
        list.removeEventListener('change', onStoreChange)
      }
    },
    [query],
  )

  // Read live rather than from a value cached at mount: the first paint at a narrow width has to
  // already be the narrow layout. A wide layout that corrects itself a frame later is how a
  // 240px sidebar gets seen on a 375px screen.
  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query])

  return useSyncExternalStore(subscribe, getSnapshot)
}

/** True at or above the breakpoint — the same threshold Tailwind's `md:` and `lg:` prefixes use. */
export function useIsAtLeast(breakpoint: Breakpoint): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS[breakpoint]})`)
}
